import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Avatar, Dropdown, Space, Typography } from 'antd';
import {
  DashboardOutlined,
  DollarOutlined,
  UserOutlined,
  MenuOutlined,
  LogoutOutlined,
  FolderOutlined,
  ShopOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { hasFileManagerAccess } from '../../constants/accessControl';
import { AppTour } from './AppTour';
import { TicketButton } from './TicketButton';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AppLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if user has access to file management
  const hasFileAccess = hasFileManagerAccess(user?.email);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      tourId: 'nav-dashboard',
    },
    {
      key: '/expenses',
      icon: <DollarOutlined />,
      label: 'Expenses',
      tourId: 'nav-expenses',
    },
    {
      key: '/webstaurant',
      icon: <ShopOutlined />,
      label: 'Webstaurant',
      tourId: 'nav-webstaurant',
    },
    {
      key: '/dc-prices',
      icon: <ApartmentOutlined />,
      label: 'DC Prices',
      tourId: 'nav-dc-prices',
    },
    ...(hasFileAccess ? [{
      key: '/files',
      icon: <FolderOutlined />,
      label: 'Files',
      tourId: 'nav-files',
    }] : []),
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    // Auto-close sidebar on mobile after navigation
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    }
  };

  const userDisplayName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
    : 'Guest User';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header
        style={{
          padding: isMobile ? '0 12px' : '0 24px',
          background: '#7a1f2a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
          width: '100%',
          position: 'fixed',
          top: 0,
          zIndex: 1000,
          lineHeight: 'normal',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => setCollapsed(!collapsed)}
            style={{
              color: 'white',
              fontSize: 16,
              padding: 6,
              marginRight: 8,
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <MenuOutlined />
          </div>
          <img 
            src={require('../../assets/logo.png')} 
            alt="Pita Way Logo" 
            style={{ 
              height: 40,
              width: 40,
              backgroundColor: 'white',
              borderRadius: 4,
              objectFit: 'contain',
              padding: 2,
            }} 
          />
          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 8 }}>
            <Text style={{ color: 'white', fontSize: isMobile ? 14 : 18, fontWeight: 600, lineHeight: 'normal', margin: 0 }}>
              GFS Tracking System
            </Text>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TicketButton isMobile={isMobile} userEmail={user?.email} userName={userDisplayName} />
          <AppTour isMobile={isMobile} />
          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleUserMenuClick,
            }}
            placement="bottomRight"
          >
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
              {!isMobile && <Text style={{ color: 'white' }}>{userDisplayName}</Text>}
            </Space>
          </Dropdown>
        </div>
      </Header>
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', marginTop: 64 }}>
        {/* Mobile overlay backdrop */}
        {isMobile && !collapsed && (
          <div
            onClick={() => setCollapsed(true)}
            style={{
              position: 'fixed',
              top: 64,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 29,
              transition: 'opacity 0.3s',
            }}
          />
        )}
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{
            background: '#fff',
            height: '120vh',
            position: 'fixed',
            left: isMobile && collapsed ? -280 : 0,
            top: 64,
            zIndex: 30,
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.06)',
            transition: 'left 0.3s ease-in-out',
          }}
          width={isMobile ? 280 : (collapsed ? 80 : 280)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ paddingTop: 16 }}></div>
            <div style={{ flex: 1, padding: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {menuItems.map((item) => {
                  const isSelected = location.pathname === item.key;
                  return (
                    <div
                      key={item.key}
                      data-tour={item.tourId}
                      onClick={() => handleMenuClick({ key: item.key } as any)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: 16,
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: isSelected ? '#7a1f2a' : 'transparent',
                        color: isSelected ? '#fff' : '#374151',
                        boxShadow: isSelected ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                          e.currentTarget.style.color = '#7a1f2a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#374151';
                        }
                      }}
                    >
                      <span style={{ fontSize: 16, display: 'flex', alignItems: 'center', color: isSelected ? '#fff' : 'inherit' }}>
                        {item.icon}
                      </span>
                      {!collapsed && (
                        <span style={{ marginLeft: 16, fontSize: 14, fontWeight: 500 }}>
                          {item.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Sider>
        <Content
          style={{
            margin: '24px',
            minHeight: 280,
            flex: 1,
            overflow: 'auto',
            marginLeft: isMobile ? 0 : (collapsed ? 80 : 280),
            transition: 'margin-left 0.3s ease-in-out',
          }}
        >
          <Outlet />
        </Content>
      </div>
    </div>
  );
};

export default AppLayout;
